import { useEffect, useState } from 'react'
import { api, type VendorMessage } from '../../lib/api'
import { ActivityFeed, Status, type Tone } from '../kit'
import { stamp } from './words'

// The last three messages sent to the vendor's contacts, with what became of
// each: during an exit the question is whether the notice and the chase for a
// certificate actually reached anybody. The whole history is on the vendor's
// Messages section.

const KIND: Record<string, string> = {
  'vendor.invite': 'Invitation',
  'vendor.reminder': 'Reminder',
  'vendor.clarification': 'Question about an answer',
  'vendor.otp': 'Sign-in code',
}

const CHANNEL: Record<string, string> = { EMAIL: 'email', SMS: 'SMS', WHATSAPP: 'WhatsApp' }

function outcome(m: VendorMessage): { tone: Tone; text: string } {
  switch (m.status) {
    case 'READ':
      return { tone: 'ok', text: 'Read' }
    case 'DELIVERED':
      return { tone: 'ok', text: 'Delivered' }
    case 'SENT':
      return { tone: 'neutral', text: 'Sent' }
    case 'MUTED':
      return { tone: 'warn', text: 'Not sent' }
    case 'SUPPRESSED':
      return { tone: 'warn', text: 'Held back' }
    default:
      return { tone: 'crit', text: 'Not delivered' }
  }
}

export function ExitMessagesPanel({ vendorId }: { vendorId: string }) {
  const [messages, setMessages] = useState<VendorMessage[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    api
      .vendorMessages(vendorId)
      .then((r) => {
        if (live) setMessages(r.messages ?? [])
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  if (failed) return null
  const latest = [...(messages ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 3)

  return (
    <section className="panel exit-panel" aria-labelledby="exit-messages-h">
      <div className="exit-panel-head">
        <h2 id="exit-messages-h" className="exit-grow">
          Messages to the vendor
        </h2>
      </div>
      {messages !== null && latest.length === 0 && <p className="exit-sub">Nothing sent to this vendor is on record</p>}
      {latest.length > 0 && (
        <ActivityFeed
          items={latest.map((m, i) => {
            const o = outcome(m)
            return {
              key: m.createdAt + i,
              tone: o.tone,
              when: stamp(m.createdAt),
              text: (
                <span className="exit-inline">
                  {(KIND[m.templateKey] ?? 'Message') + ', ' + (CHANNEL[m.channel] ?? m.channel)}
                  <Status tone={o.tone} title={m.detail}>
                    {o.text}
                  </Status>
                </span>
              ),
            }
          })}
        />
      )}
    </section>
  )
}
